import { Text } from '@shared/components';
import { useToast } from '@shared/components/toast';
import {
  OriginatorSettingsDocument,
  useUpdateOriginatorMutation,
} from '@shared/generated/graphql';
import useOriginatorSettings from '@shared/graphql/hooks/originator/useOriginatorSettings';
import { useEffect } from 'react';
import { AutoReplySettings } from '../AutoReplySettings';
import { AutoReplySettingsForm } from '../types';
import { AUTO_REPLY_HELPER_TEXT, HIERARCHY_WARNING } from '../utils';

type OriginatorAutoReplySettingsProps = {
  quoteIds: string[];
  originatorId?: string;
  handleClose: () => void;
  handleChangeLevel: () => void;
};

export const OriginatorAutoReplySettings = ({
  quoteIds,
  originatorId,
  handleClose,
  handleChangeLevel,
}: OriginatorAutoReplySettingsProps) => {
  const {
    settings,
    isLoading: loadingSettings,
    refetch,
  } = useOriginatorSettings({
    id: originatorId ?? '',
  });
  const [updateOriginator, { loading: loadingUpdate }] =
    useUpdateOriginatorMutation();
  const { sendToast } = useToast();

  useEffect(() => {
    refetch && refetch();
  }, [originatorId]);

  const handleSubmit = async (
    autoReply: AutoReplySettingsForm,
    shouldClose?: boolean
  ) => {
    if (!originatorId || loadingSettings) {
      sendToast('Unexpected error updating settings', {
        variant: 'error',
      });
      return;
    }

    const updatedSettings = {
      autoReply: {
        ...settings?.autoReply,
        ...autoReply,
      },
    };

    try {
      const res = await updateOriginator({
        variables: {
          input: {
            id: originatorId,
            settings: updatedSettings,
          },
        },
        refetchQueries: [OriginatorSettingsDocument],
      });

      if (res.data?.updateOriginator.success) {
        sendToast('Settings updated', {
          variant: 'success',
        });
        shouldClose && handleClose();
        return;
      }
    } catch (err) {
      console.error(err);
    }

    sendToast('Error updating settings', {
      variant: 'error',
    });
  };

  return (
    <AutoReplySettings
      quoteIds={quoteIds}
      onSubmit={handleSubmit}
      loadingSubmit={loadingUpdate}
      autoReplySettings={settings?.autoReply}
      handleChangeLevel={handleChangeLevel}
      editLevelDesc={`Edit your user level instructions instead`}
      header={
        <div className="flex flex-col gap-2">
          <Text type="body-sm" className="text-gray-700">
            {AUTO_REPLY_HELPER_TEXT}
          </Text>
          <Text type="body-sm" isHeavy={true} className="text-gray-700">
            {HIERARCHY_WARNING}
          </Text>
        </div>
      }
    />
  );
};
